import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useNotification } from "../../../providers/NotificationProvider";
import { BSC_CATEGORY_CONFIG } from "./bscConfig";
import { fetchBscAchievements } from "../../../api/companyApi";

const formatDate = (iso) => {
  if (!iso) return "";
  return iso.substring(0, 10).replace(/-/g, "/");
};

/**
 * BSC 全目標・実績一覧ページ
 * 全カテゴリの目標を並行取得し、最新実績値を付加して1画面に一覧表示する。
 */
export default function BscAllGoalListPage() {
  const { businessDomainId, businessUnitId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // ① 全カテゴリの目標を並行取得
      const categoryEntries = Object.entries(BSC_CATEGORY_CONFIG);
      const [goalsResults, achievements] = await Promise.all([
        Promise.all(
          categoryEntries.map(([, config]) => config.fetchGoals(businessUnitId).catch(() => []))
        ),
        fetchBscAchievements(businessUnitId).catch(() => []),
      ]);

      // ② 指標名 → 最新実績のマップを作成
      const achievementMap = {};
      (Array.isArray(achievements) ? achievements : []).forEach((a) => {
        const name = a.goal?.indicator?.indicatorName;
        if (!name) return;
        const existing = achievementMap[name];
        if (!existing || new Date(a.actualDate) > new Date(existing.actualDate)) {
          achievementMap[name] = a;
        }
      });

      // ③ 全目標をカテゴリ名付きでフラット化し実績を結合
      const allRows = [];
      categoryEntries.forEach(([, config], idx) => {
        const goals = Array.isArray(goalsResults[idx]) ? goalsResults[idx] : [];
        const categoryLabel = t(config.titleKey).replace(" BSC", "");
        goals.forEach((g, gIdx) => {
          const indicatorName = g.indicator?.indicatorName || "";
          const latestAchievement = achievementMap[indicatorName] || null;
          allRows.push({
            id: `${idx}_${gIdx}_${indicatorName}`,
            categoryLabel,
            categoryKey: Object.keys(BSC_CATEGORY_CONFIG)[idx],
            successFactorName: g.successFactorName || "",
            indicatorName,
            unit: g.indicator?.unit || "",
            plannedValue: g.goal?.plannedValue ?? "",
            deadline: g.goal?.deadline || "",
            actualValue: latestAchievement?.actualValue ?? "",
            actualDate: latestAchievement?.actualDate || "",
          });
        });
      });

      setRows(allRows);
    } catch (e) {
      notify.apiError(e);
    } finally {
      setLoading(false);
    }
  }, [businessUnitId]);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredRows = searchText
    ? rows.filter((r) =>
        r.indicatorName?.toLowerCase().includes(searchText.toLowerCase()) ||
        r.successFactorName?.toLowerCase().includes(searchText.toLowerCase()) ||
        r.categoryLabel?.toLowerCase().includes(searchText.toLowerCase())
      )
    : rows;

  const columns = [
    {
      field: "categoryLabel",
      headerName: t("bsc.category"),
      width: 160,
    },
    {
      field: "successFactorName",
      headerName: t("bsc.successFactor"),
      flex: 1,
      minWidth: 140,
    },
    {
      field: "indicatorName",
      headerName: t("bsc.indicator"),
      flex: 1,
      minWidth: 140,
    },
    {
      field: "unit",
      headerName: t("bsc.unit"),
      width: 70,
    },
    {
      field: "plannedValue",
      headerName: t("bsc.plannedValue"),
      width: 90,
    },
    {
      field: "deadline",
      headerName: t("bsc.deadline"),
      width: 110,
      valueFormatter: (v) => formatDate(v),
    },
    {
      field: "actualValue",
      headerName: t("bsc.achievement.actualValue"),
      width: 90,
    },
    {
      field: "actualDate",
      headerName: t("bsc.achievement.actualDate"),
      width: 110,
      valueFormatter: (v) => formatDate(v),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("bsc.allGoals.title")}</Typography>
        <TextField size="small" placeholder={t("common.search")} value={searchText} onChange={(e) => setSearchText(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
      </Box>
      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(r) => r.id}
        loading={loading}
        autoHeight
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        onRowClick={(params) =>
          navigate(
            `/strategy/${businessDomainId}/business-units/${businessUnitId}/bsc/${params.row.categoryKey}`
          )
        }
        sx={{ cursor: "pointer" }}
      />
    </Box>
  );
}
